import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { FormContent } from 'shared/types/forms';
import { useFormExport } from './hooks';
import { Text } from '@televet/kibble-ui/build/components/Text';
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalFooterButtons,
  ModalHeader,
} from '@televet/kibble-ui/build/components/Modal';
import { ExportModalPet } from './FormExportMenu';
import { TagBlock, TagBlockTag } from '@televet/kibble-ui/build/patterns/TagBlock';
import { MenuItemProps } from '@televet/kibble-ui/build/components/Menu';

interface IFormExportModalProps {
  form: {
    id: string;
    title: string;
    content: FormContent;
  };
  pets: ExportModalPet[];
  petParentName: string;
  onClose: () => void;
  isOpen: boolean;
}

export const FormExportModal = ({ form, pets, petParentName, isOpen, onClose }: IFormExportModalProps): JSX.Element => {
  const { isSending, integrationName, exportFormToPims: exportToPims } = useFormExport({ form });
  const [selectedPets, setSelectedPets] = useState<TagBlockTag[]>([]);

  const hasSelectedPets = useMemo(() => {
    return selectedPets.length > 0;
  }, [selectedPets]);

  const handleSelectPet = useCallback(
    (_, allSelectedItems?: MenuItemProps[]): void => {
      if (allSelectedItems) {
        const selectedPetTags: TagBlockTag[] = allSelectedItems.map((pet) => ({
          label: pet.label?.toString() || '',
          value: pet.value?.toString() || '',
        }));
        setSelectedPets(selectedPetTags);
      }
    },
    [setSelectedPets],
  );

  const petOptions = useMemo(() => {
    return pets.map((p) => ({
      label: `${p.name}${p.pimsId ? '' : ' (Not synced)'}`,
      value: p.id || '',
      isDisabled: !p.pimsId,
      isSelected: selectedPets.some((pet) => pet.value === p.id),
    }));
  }, [pets, selectedPets]);

  const handleExport = async (): Promise<void> => {
    if (hasSelectedPets) {
      await exportToPims(
        selectedPets.map((pet) => {
          return { id: pet.value || '', name: pet.label as string };
        }),
      );
      onClose();
    }
  };

  // Default selected pets to any pet assigned to the form when the modal opens
  useEffect(() => {
    const assignedPets = pets.filter((pet) => pet.assigned);
    if (assignedPets.length) {
      setSelectedPets(
        assignedPets.map((pet) => ({
          label: pet.name || '',
          value: pet.id || '',
        })),
      );
    }
    // eslint-disable-next-line
  }, [isOpen]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered>
      <ModalCloseButton onClick={onClose} />
      <ModalHeader>Select Pets For Export</ModalHeader>
      <ModalBody>
        <Text as="p" mb={2} variant="subtle">
          {form.title} submitted by {petParentName}
        </Text>
        <TagBlock
          title="Selected pets:"
          menuListProps={{ menuItems: petOptions, isMultiSelect: true, onSelect: handleSelectPet }}
          tags={selectedPets}
          onClearAll={(): void => setSelectedPets([])}
          onRemoveTag={(tag): void => {
            setSelectedPets((prev) => prev.filter((pet) => pet.value !== tag));
          }}
        />
      </ModalBody>
      <ModalFooterButtons
        onSubmit={handleExport}
        submitText={`Export to ${integrationName}`}
        isDisabled={!hasSelectedPets}
        isLoading={isSending}
      />
    </Modal>
  );
};
